import { api } from "./api";

export const MAX_VIDEO = 1024 * 1024 * 1024;
export const VIDEO_TYPES = ["video/mp4", "video/webm", "video/quicktime"];

async function putPart(id, i, blob, signal) {
  for (let attempt = 0; ; attempt++) {
    try {
      return await api.put(`/admin/video-upload/${id}/${i}`, blob, { headers: { "Content-Type": "application/octet-stream" }, signal });
    } catch (e) {
      if (signal?.aborted || attempt >= 3 || (e.response && e.response.status < 500)) throw e;
      await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
    }
  }
}

export async function uploadVideo(file, onProgress, signal) {
  const { data } = await api.post("/admin/video-upload/init", { filename: file.name, size: file.size, content_type: file.type });
  let next = 0, sent = 0;
  const worker = async () => {
    while (next < data.parts) {
      const i = next++;
      const blob = file.slice(i * data.part_size, Math.min(file.size, (i + 1) * data.part_size));
      await putPart(data.id, i, blob, signal);
      sent += blob.size;
      onProgress(sent / file.size);
    }
  };
  await Promise.all([worker(), worker(), worker()]);
  return (await api.post(`/admin/video-upload/${data.id}/complete`)).data;
}

export function captureThumb(file) {
  return new Promise((resolve) => {
    const v = document.createElement("video");
    const url = URL.createObjectURL(file);
    const done = (r) => { URL.revokeObjectURL(url); resolve(r); };
    v.preload = "metadata";
    v.muted = true;
    v.playsInline = true;
    v.src = url;
    v.onloadedmetadata = () => { v.currentTime = Math.min(2, (v.duration || 1) / 3); };
    v.onseeked = () => {
      const c = document.createElement("canvas");
      const w = Math.min(1280, v.videoWidth || 1280);
      c.width = w;
      c.height = Math.round(w * ((v.videoHeight || 720) / (v.videoWidth || 1280)));
      c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
      c.toBlob((blob) => done({ blob, duration: Math.round(v.duration || 0) }), "image/jpeg", 0.85);
    };
    v.onerror = () => done({ blob: null, duration: 0 });
  });
}

export async function uploadImageBlob(blob, name) {
  const fd = new FormData();
  fd.append("file", blob, `${name || "kapak"}.jpg`);
  fd.append("alt", name || "");
  return (await api.post("/admin/upload", fd)).data.url;
}

export const fmtDuration = (s) => {
  if (!s) return "";
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return [h, h ? String(m).padStart(2, "0") : m, String(sec).padStart(2, "0")].filter((x, i) => i || x).join(":");
};

export const isoDuration = (s) => (s ? `PT${Math.floor(s / 3600) ? `${Math.floor(s / 3600)}H` : ""}${Math.floor((s % 3600) / 60)}M${s % 60}S` : undefined);
